import { useState } from 'react'
import { Button, Col, Row, Space, Typography } from 'antd'
import { ArrowLeftOutlined, CheckOutlined, CloseOutlined } from '@ant-design/icons'
import { Show } from '@refinedev/antd'
import { useOne } from '@refinedev/core'
import { useParams } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { ReceivedCancellationRequestRoot } from '@repo/sdk'

import { DetailNotFound } from '../../components/DetailNotFound'
import { PageError } from '../../components/PageError'
import { StatusBadge } from '../../components/StatusBadge'
import { NicknameText } from '../../components/NicknameText'
import { useBackToList } from '../../shared/hooks/useBackToList'
import { formatKrw } from '@repo/shared'
import { useCancellationStatusConfig } from './cancellationStatusConfig'
import { useApproveCancellation } from './useApproveCancellation'
import { CancellationInfoCard } from './CancellationInfoCard'
import { CancellationOrderInfoCard } from './CancellationOrderInfoCard'
import { CancellationBuyerInfoCard } from './CancellationBuyerInfoCard'
import { CancellationTimelineCard } from './CancellationTimelineCard'
import { RejectCancellationModal } from './RejectCancellationModal'
import { confirmPopup } from '@/components/popup'

export function CancellationDetail() {
  const { id } = useParams<{ id: string }>()
  const backToList = useBackToList('/cancellations')
  const { t } = useTranslation()
  const statusConfig = useCancellationStatusConfig()
  const { approveCancellation, isApproving } = useApproveCancellation()
  const [rejectModalOpen, setRejectModalOpen] = useState(false)

  const { query } = useOne<ReceivedCancellationRequestRoot>({
    resource: 'cancellation-list',
    id: id ?? '',
    queryOptions: { enabled: !!id },
  })

  const { data, isLoading, isError } = query
  const cancellation = data?.data

  if (!isLoading && isError) return <PageError onRetry={() => void query.refetch()} />
  if (!isLoading && !isError && !cancellation) return <DetailNotFound backTo="/cancellations" />

  const isRequested = cancellation?.status === 'REQUESTED'

  const handleApprove = () => {
    if (!id || !cancellation) return
    const refundAmount = formatKrw(cancellation.expectedRefundAmount.amount)
    confirmPopup({
      title: t('common.modals.approveCancellationTitle'),
      content: (
        <>
          <p>{t('common.modals.approveCancellationMessage', { amount: refundAmount })}</p>
          <p style={{ color: '#ff4d4f', fontWeight: 'bold' }}>
            {t('common.modals.approveCancellationWarning')}
          </p>
        </>
      ),
      okText: t('common.buttons.confirm'),
      cancelText: t('common.buttons.cancel'),
      okButtonProps: { 'data-testid': 'cancellation-detail-approve-confirm-btn' },
      cancelButtonProps: { 'data-testid': 'cancellation-detail-approve-cancel-btn' },
      onOk: () => approveCancellation(id),
    })
  }

  return (
    <Show
      isLoading={isLoading}
      title={t('pages.cancellations.detailTitle')}
      headerButtons={
        <Space>
          {isRequested && (
            <>
              <Button
                data-testid="cancellation-detail-approve-btn"
                type="primary"
                icon={<CheckOutlined />}
                loading={isApproving}
                onClick={handleApprove}
              >
                {t('common.buttons.approve')}
              </Button>
              <Button
                data-testid="cancellation-detail-reject-btn"
                danger
                icon={<CloseOutlined />}
                // 승인 요청이 처리되는 동안에는 거절을 막는다 — 두 요청이 겹치면 뒤 요청이 실패한다(개발항목 294)
                disabled={isApproving}
                onClick={() => setRejectModalOpen(true)}
              >
                {t('common.buttons.reject')}
              </Button>
            </>
          )}
        </Space>
      }
    >
      <Button
        data-testid="cancellation-detail-back-btn"
        type="link"
        icon={<ArrowLeftOutlined />}
        onClick={backToList}
        style={{ padding: 0, marginBottom: 16 }}
      >
        {t('common.buttons.backToList')}
      </Button>
      {cancellation && (
        <>
          <Space style={{ marginBottom: 16 }}>
            <Typography.Title level={5} style={{ margin: 0 }}>
              {cancellation.cancellationId.slice(0, 8)}...
            </Typography.Title>
            <Typography.Text>
              <NicknameText nickname={cancellation.buyer.nickname} />
            </Typography.Text>
            <StatusBadge status={cancellation.status} configMap={statusConfig} />
          </Space>
          <Row gutter={[16, 16]}>
            <Col xs={24} lg={12}>
              <CancellationInfoCard cancellation={cancellation} />
            </Col>
            <Col xs={24} lg={12}>
              <CancellationOrderInfoCard cancellation={cancellation} />
            </Col>
            <Col xs={24} lg={12}>
              <CancellationBuyerInfoCard cancellation={cancellation} />
            </Col>
            <Col xs={24} lg={12}>
              <CancellationTimelineCard cancellation={cancellation} />
            </Col>
          </Row>
          <RejectCancellationModal
            cancellationId={cancellation.cancellationId}
            open={rejectModalOpen}
            onClose={() => setRejectModalOpen(false)}
          />
        </>
      )}
    </Show>
  )
}
